<template>
  <v-dialog v-model="show" max-width="800px" max persistent scrollable>
    <v-card style="height: fit-content;">
      <validation-observer ref="form" v-slot="observer" >
      <v-card-text v-if="formValues">
        <v-card-title mt-2 style="justify-content: center;">{{ modalTitle }}</v-card-title>

          <v-form>
            <v-col cols="12">
              <validation-provider v-slot="{ errors }" :name="$t('settings.box.name')" rules="required|label_name">
                <v-text-field
                  v-model="formValues.name"
                  :error-messages="errors"
                  :label="$t('settings.box.boxName')"
                  clearable
                  required
                >
                </v-text-field>
              </validation-provider>

              <v-tabs v-model="activeTab" grow>
                <v-tab>{{ $t('settings.box.general') }}</v-tab>
                <v-tab>{{ $t('settings.box.translations') }}</v-tab>
              </v-tabs>

              <v-tabs-items v-model="activeTab">
                <v-tab-item>
                  <v-switch
                    v-model="formValues.activate"
                    :label="$t('settings.active')"
                  />
                  <validation-provider v-slot="{ errors }" :name="$t('settings.box.reference')" rules="identifier">
                    <v-text-field
                      v-model="formValues.reference"
                      :label="$t('settings.box.reference')"
                      :error-messages="errors"
                      clearable
                    />
                  </validation-provider>

                  <div class="text-subtitle-1 mt-4 mb-2">{{ $t('settings.box.dimensions') }}</div>

                  <v-row>
                    <v-col cols="12" sm="3">
                      <v-text-field
                        v-model.number="formValues.length"
                        :label="$t('settings.box.length') + ' *'"
                        :error-messages="dimensionErrors.length"
                        type="number"
                        :min="0"
                        outlined
                        dense
                        @input="dimensionErrors.length = ''"
                      />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <v-text-field
                        v-model.number="formValues.width"
                        :label="$t('settings.box.width') + ' *'"
                        :error-messages="dimensionErrors.width"
                        type="number"
                        :min="0"
                        outlined
                        dense
                        @input="dimensionErrors.width = ''"
                      />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <v-text-field
                        v-model.number="formValues.height"
                        :label="$t('settings.box.height') + ' *'"
                        :error-messages="dimensionErrors.height"
                        type="number"
                        :min="0"
                        outlined
                        dense
                        @input="dimensionErrors.height = ''"
                      />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <validation-provider v-slot="{ errors }" :name="$t('settings.box.dimensionUnit')" :rules="dimensionUnitRules">
                        <v-select
                          v-model="formValues.dimensionUnit"
                          :items="dimensionUnitOptions"
                          :label="$t('settings.box.dimensionUnit')"
                          :error-messages="errors"
                          outlined
                          dense
                        />
                      </validation-provider>
                    </v-col>
                  </v-row>

                  <div class="text-subtitle-1 mt-4 mb-2">{{ $t('settings.box.boxWeight') }}</div>

                  <v-row>
                    <v-col cols="12" sm="3">
                      <v-text-field
                        v-model.number="formValues.emptyWeight"
                        :label="$t('settings.box.weight')"
                        :error-messages="emptyWeightError"
                        type="number"
                        :min="0"
                        outlined
                        dense
                        clearable
                        @input="emptyWeightError = ''"
                      />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <validation-provider v-slot="{ errors }" :name="$t('settings.box.weightUnit')" :rules="weightUnitRules">
                        <v-select
                          v-model="formValues.weightUnit"
                          :items="weightUnitOptions"
                          :label="$t('settings.box.weightUnit')"
                          :error-messages="errors"
                          outlined
                          dense
                        />
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="3">
                      <v-text-field
                        v-model.number="formValues.maxWeight"
                        :label="$t('settings.box.maxWeight')"
                        :error-messages="weightError"
                        type="number"
                        :min="0"
                        outlined
                        dense
                        clearable
                        @input="weightError = ''"
                      />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <validation-provider v-slot="{ errors }" :name="$t('settings.box.maxWeightUnit')" :rules="maxWeightUnitRules">
                        <v-select
                          v-model="formValues.maxWeightUnit"
                          :items="weightUnitOptions"
                          :label="$t('settings.box.weightUnit')"
                          :error-messages="errors"
                          outlined
                          dense
                        />
                      </validation-provider>
                    </v-col>
                  </v-row>
                </v-tab-item>

                <v-tab-item>
                  <div v-for="lang in sortedLanguages" :key="lang.code" class="d-flex align-center mb-3 mt-5">
                    <div class="lang-label mr-3">
                      <div class="flag-icon mr-2" :class="'flag-icon-' + lang.flag"></div>
                      <span class="text-uppercase font-weight-medium">{{ lang.code }}</span>
                    </div>
                    <validation-provider v-slot="{ errors }" :name="$t('settings.box.translation') + ' ' + lang.code.toUpperCase()" rules="label_name" class="flex-grow-1">
                      <v-text-field
                        v-model="formValues.labels[lang.code]"
                        :label="getTranslationLabel(lang.code)"
                        :error-messages="lang.code === 'en' ? (englishTranslationError || errors[0]) : errors[0]"
                        outlined
                        dense
                        hide-details="auto"
                        @input="clearEnglishError(lang.code)"
                      />
                    </validation-provider>
                  </div>
                </v-tab-item>
              </v-tabs-items>

            </v-col>
          </v-form>
      </v-card-text>
      <v-card-actions class="pa-0 mt-2">
        <v-btn
          color="primary"
          style="width: 50%"
          tile
          @click="onSubmit"
          :disabled="observer.invalid"
        >
          {{ $t('settings.alerts.validate') }}
        </v-btn>
        <v-btn
          class="ml-0"
          color="secondary"
          style="width: 50%"
          tile
          @click="close"
        >
          {{ $t('settings.alerts.cancel') }}
        </v-btn>
      </v-card-actions>
      </validation-observer>
    </v-card>
  </v-dialog>
</template>

<script>
  import { ValidationObserver, ValidationProvider } from 'vee-validate'
  import { displayMessage, isEmptyWeightExceedingMax, sortLanguages } from '@/helpers/helper'
  import { SUPPORTED_LANGUAGES } from '@/constants/languages'

  export default {
  name: 'BoxFormDialog',
  props: {
    formValue: {
      type: Object,
      default: undefined
    },
    value: {
      type: Boolean,
      default: false
    },
  },
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data () {
    return {
      activeTab: 0,
      formValues: null,
      dimensionUnitOptions: [
        { text: 'mm', value: 'mm' },
        { text: 'cm', value: 'cm' }
      ],
      weightUnitOptions: [
        { text: 'g', value: 'g' },
        { text: 'kg', value: 'kg' }
      ],
      languages: SUPPORTED_LANGUAGES,
      englishTranslationError: '',
      weightError: '',
      dimensionErrors: { length: '', width: '', height: '' },
      emptyWeightError: ''
    }
  },
  mounted () {
    // Initialize labels with all supported language keys for reactivity
    const emptyLabels = SUPPORTED_LANGUAGES.reduce((acc, lang) => {
      acc[lang.code] = ''
      return acc
    }, {})

    if (this.formValue) {
      this.formValues = {
        ...JSON.parse(JSON.stringify(this.formValue)),
        labels: { ...emptyLabels, ...this.formValue.labels }
      }
    } else {
      // New mode: initialize with defaults
      this.formValues = {
        name: undefined,
        activate: false,
        reference: undefined,
        dimensionUnit: 'mm',
        weightUnit: 'g',
        maxWeightUnit: 'kg',
        labels: emptyLabels
      }
    }
  },
  watch: {
    'formValues.name'(newName) {
      if (this.formValues && newName != null) {
        const currentLocale = this.$i18n.locale
        if (this.formValues.labels[currentLocale] !== newName) {
          this.$set(this.formValues.labels, currentLocale, newName)
        }
      }
    },
    currentLocaleLabel(newLabel) {
      if (this.formValues && newLabel != null && this.formValues.name !== newLabel) {
        this.formValues.name = newLabel
      }
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    modalTitle() {
      return this.formValue ? this.$t('settings.box.editBox') : this.$t('settings.box.addBox')
    },
    currentLocaleLabel() {
      return this.formValues?.labels?.[this.$i18n.locale]
    },
    hasDimension() {
      if (!this.formValues) return false
      return this.formValues.length != null || this.formValues.width != null || this.formValues.height != null
    },
    dimensionUnitRules() {
      return this.hasDimension ? 'required' : ''
    },
    hasWeight() {
      if (!this.formValues) return false
      return this.formValues.emptyWeight != null && this.formValues.emptyWeight !== ''
    },
    weightUnitRules() {
      return this.hasWeight ? 'required' : ''
    },
    hasMaxWeight() {
      if (!this.formValues) return false
      return this.formValues.maxWeight != null && this.formValues.maxWeight !== ''
    },
    maxWeightUnitRules() {
      return this.hasMaxWeight ? 'required' : ''
    },
    sortedLanguages() {
      return sortLanguages(this.languages)
    }
  },
  methods: {
    close () {
      this.$emit('close')
    },
    getTranslationLabel(langCode) {
      const required = langCode === 'en' ? ' *' : ''
      return `${this.$t('settings.box.translation')} ${langCode.toUpperCase()}${required}`
    },
    clearEnglishError(langCode) {
      if (langCode === 'en'){
        this.englishTranslationError = ''
      }
    },
    validateDimension(value, unit) {
      if (value == null || value <= 0){
        return this.$t('settings.box.fieldRequired')
      }
      if (unit === 'mm' && !Number.isInteger(value)) {
        return this.$t('settings.box.mustBeInteger')
      }
      return ''
    },
    validateWeight(value, unit) {
      if (value == null || value === ''){
        return ''
      }
      if (value <= 0){
        return this.$t('settings.box.mustBePositive')
      }
      if (unit === 'g' && !Number.isInteger(value)){
        return this.$t('settings.box.mustBeInteger')
      }
      return ''
    },
    validateCustomRules () {
      this.dimensionErrors = { length: '', width: '', height: '' }
      this.emptyWeightError = ''
      this.weightError = ''
      this.englishTranslationError = ''

      // Check dimensions (required, > 0, integer if mm)
      const { dimensionUnit } = this.formValues
      ;['length', 'width', 'height'].forEach(field => {
        this.dimensionErrors[field] = this.validateDimension(this.formValues[field], dimensionUnit)
      })

      // Check weights (> 0 if filled, integer if g)
      this.emptyWeightError = this.validateWeight(this.formValues.emptyWeight, this.formValues.weightUnit)
      this.weightError = this.validateWeight(this.formValues.maxWeight, this.formValues.maxWeightUnit)

      // Check if empty weight is greater than max weight (only if both are valid)
      if (!this.emptyWeightError && !this.weightError && isEmptyWeightExceedingMax(
        this.formValues.emptyWeight,
        this.formValues.weightUnit,
        this.formValues.maxWeight,
        this.formValues.maxWeightUnit
      )) {
        this.emptyWeightError = this.$t('settings.box.emptyWeightExceedsMax')
      }

      const hasErrors = Object.values(this.dimensionErrors).some(e => e) || this.emptyWeightError || this.weightError

      if (hasErrors) {
        this.activeTab = 0
        return false
      }

      // Check English translation
      if (!this.formValues.labels?.en?.trim()) {
        this.activeTab = 1
        this.englishTranslationError = this.$t('settings.box.englishRequired')
        return false
      }

      return true
    },
    async onSubmit () {
      if (!this.validateCustomRules()) return

      const success = await this.$refs.form.validate()
      if (success) {
        const payload = Object.fromEntries(
          Object.entries(this.formValues).filter(([, v]) => v != null && v !== '')
        )
        this.$emit('validateForm', payload)
      } else {
        displayMessage(this.$t('settings.invalidSettings'))
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/flags.scss';

.no-dropdown-chevron ::v-deep(.v-input__icon--append) {
  display: none;
}

.lang-label {
  min-width: 70px;
  display: flex;
  align-items: center;
}
</style>

